import { Component, computed, inject, linkedSignal, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { PageComponent, PageFooterComponent, PageHeaderComponent } from '@softapps/core/pages';
import {
  ApplicationModule, backNavigation, menuItems, queryParam, showRequestErrors, subtitle, title } from '@softline/application';
import { authorizationFn } from '@softline/auth';
import { addDays, DateService, IsoDate, parseIso8601, startOfWeek, toIsoDate, Store } from '@softline/core';
import { FormatPipe, MessageBarService, translator } from '@softline/ui-core';
import { selectDateFn } from '@softline/ui-core/date-input';
import { PrevCurrNextNavigationComponent } from '@softline/ui-core/date';
import { Subject } from 'rxjs';
import {
  ArbeitsberichtConfig,
  SOFTLINE_CONFIG_ARBEITSBERICHT } from '../../arbeitsbericht.config';
import { SOFTLINE_PERMISSION_TIME_REPORT_PARTY } from '../../arbeitsbericht.permissions';
import { SOFTLINE_FEATURE_TIME_REPORT_PARTY_FAVORITES } from '../../arbeitsbericht.shared';
import { ListComponent } from '../../components/list/list.component';
import { Objekt } from '../../models/objekt.model';
import { TimeReport } from '../../models/time-report.model';
import { TimeReportsService } from '../../services/time-reports.service';
import { PartyFavoritesStore } from '../../store/party-favorites.store';

import { DatePipe } from '@angular/common';
@Component({
  selector: 'soft-time-report-overview',
  templateUrl: './overview.page.html',
  styleUrls: ['./overview.page.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ApplicationModule,
    ListComponent,
    PageHeaderComponent,
    PageFooterComponent,
    PageComponent,
    DatePipe,
    FormatPipe,
    PrevCurrNextNavigationComponent,
  ],
})
export class OverviewPage implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly dateService = inject(DateService);
  private readonly messageBarService = inject(MessageBarService);
  private readonly timeReportConfig: ArbeitsberichtConfig = inject(
    SOFTLINE_CONFIG_ARBEITSBERICHT
  );
  private readonly timeReportsService = inject(TimeReportsService);
  private readonly t = translator();
  private readonly isAuthorized = authorizationFn();

  readonly totalTime = computed(() => this.timeReportsService.sum() ?? []);
  readonly entryCount = computed(
    () => this.timeReportsService.value()?.length ?? 0
  );
  private readonly dateQueryParam = queryParam('date');
  readonly date = linkedSignal(
    () => (this.dateQueryParam() as IsoDate) ?? this.dateService.now()
  );

  readonly #datePicker = selectDateFn();

  readonly hasPartyPermission = this.isAuthorized(SOFTLINE_PERMISSION_TIME_REPORT_PARTY);
  readonly selectedObject$ = new Subject<Objekt | null>();
  readonly allowCreate = computed(() => this.isDateEditable() ?? false);

  readonly isDateEditable = computed(() => {
    const date = this.date();

    if (this.timeReportConfig?.editableWeeksBeforeToday !== undefined) {
      const viewedTime = parseIso8601(date);
      const weekDiff =
        viewedTime === null
          ? Number.NaN
          : Math.trunc((Date.now() - viewedTime) / 604800000);

      if (this.timeReportConfig.editableWeeksBeforeToday === 0) {
        // Only allow dates in the current week
        const sameWeek =
          viewedTime !== null &&
          startOfWeek(toIsoDate(new Date())) ===
            startOfWeek(String(date).substring(0, 10) as IsoDate);
        if (!sameWeek) {
          return false;
        }
      } else if (this.timeReportConfig.editableWeeksBeforeToday === 1) {
        // Allow dates within one week before current date
        if (weekDiff > 1) {
          return false;
        }
      } else {
        // For any other number of weeks (fallback logic)
        if (weekDiff > this.timeReportConfig.editableWeeksBeforeToday) {
          return false;
        }
      }
    } else if (this.timeReportConfig?.editableDaysBeforeToday !== undefined) {
      const lastEditableDate = this.subtractDaysToCurrentDate(
        this.timeReportConfig.editableDaysBeforeToday
      );
      lastEditableDate.setHours(0, 0, 0, 0); // Reset time to midnight

      const currentDate = new Date(date);
      currentDate.setHours(0, 0, 0, 0); // Reset time to midnight

      // Compare the provided date with the last editable date
      if (currentDate < lastEditableDate) {
        return false;
      }
    }

    return true;
  });

  constructor() {
    title(this.t('#TIME_REPORT.TITLE'));
    subtitle('Übersicht');
    menuItems(() => [
      {
        type: 'execute',
        name: 'test-command',
        outlet: 'responsive',
        class: 'soft-button accent',
        icon: 'fa-regular fa-plus',
        title: 'Erfassen',
        canExecute: this.allowCreate(),
        execute: () => this.navigateToCreatePage() },
      {
        type: 'execute',
        icon: 'fa-regular fa-plus',
        name: 'create',
        title: 'Neuer Eintrag',
        priority: 1,
        outlet: 'nav',
        canExecute: this.allowCreate(),
        execute: () => this.navigateToCreatePage() },
      {
        name: 'arbeitsberichtOverview',
        type: 'route',
        priority: 2,
        outlet: 'nav',
        icon: 'fa-regular fa-calendar-check',
        title: '#TIME_REPORT.MENU.LIST',
        routerLink: ['/arbeitsbericht'] },
      {
        name: 'arbeitsberichtQuery',
        type: 'route',
        outlet: 'nav',
        priority: 3,
        icon: 'fa-regular fa-magnifying-glass',
        title: '#TIME_REPORT.MENU.QUERY',
        routerLink: ['/arbeitsbericht', 'query'] }]);
    backNavigation('/');
  }

  // TODO: IS THIS NECESSARY?
  async ngOnInit(): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TIME_REPORT_PARTY_FAVORITES,
        PartyFavoritesStore.actions.loadMany,
        { clear: true }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  navigatePrev(): void {
    this.onDateChange(addDays(this.date(), -1));
  }

  navigateToday(): void {
    this.onDateChange(this.dateService.now());
  }

  navigateNext(): void {
    this.onDateChange(addDays(this.date(), 1));
  }

  async openDatePicker(): Promise<void> {
    const result = await this.#datePicker({ value: this.date(), showCancel: true });
    if (!result || result === 'DISMISSED') return;
    this.onDateChange(result as IsoDate);
  }

  readonly onDateChange = async (date: IsoDate): Promise<void> => {
    this.date.set(date);
    await this.router.navigate(['/arbeitsbericht'], { queryParams: { date } });
  };

  private readonly navigateToCreatePage = (): void => {
    const date = this.date();
    this.router.navigate(['/arbeitsbericht', 'create'], {
      queryParams: { date } });
  };

  readonly navigateToEditPage = async (report: TimeReport | undefined): Promise<void> => {
    if (!report) return;
    if (report.editable === false) {
      this.messageBarService.warning('Dieser Eintrag darf nicht mehr geändert werden!');
      return;
    }
    if (!this.isDateEditable()) {
      this.messageBarService.info('Dieser Eintrag darf nicht mehr geändert werden!');
      return;
    }
    if (
      !this.timeReportConfig?.clickAction ||
      this.timeReportConfig.clickAction === 'select'
    ) {
      return;
    }
    await this.router.navigate(['/arbeitsbericht', report.id, 'edit']);
  };

  private readonly subtractDaysToCurrentDate = (days: number): Date => {
    const subtractedDate = new Date();
    subtractedDate.setDate(subtractedDate.getDate() - days);
    return subtractedDate;
  };
}
